import React from 'react';

import { Box, FormControl, MenuItem, Typography } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';

import { AppSelect, AppSwitch } from '@vyce/core/src/components/inputs';
import { SettingsCard } from '@vyce/core/src/components/SettingsCard';
import { ControlledAutocomplete } from '@vyce/core/src/components/controlled-inputs/ControlledAutocomplete';
import { PaySchedule, Site } from '@vyce/core/src/types';

import { checkboxValues, extraDescription, extraText, extraTitle, selectLabel } from '../config';
import { Options, SelectedModuleType } from '../types';
import { useStyles } from '../styles';

interface Props {
  isSelected: boolean;
  type: SelectedModuleType;
  options: Options[];
  /** Form field holding how long someone may go quiet before they are dropped. */
  name: string;
  items: (PaySchedule | Site)[];
  /** Form field holding the locations / pay schedules this mirror watches. */
  selectName: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export const ModuleItem = ({
  isSelected,
  type,
  options,
  name,
  items,
  selectName,
  expanded,
  onExpandedChange,
}: Props) => {
  const classes = useStyles();
  const { control, setValue, watch } = useFormContext();

  const period = watch(name);
  const periodLabel = options.find(option => option.value === period)?.name;

  const status = isSelected && periodLabel ? `On · drops after ${periodLabel}` : 'Off';

  return (
    <SettingsCard
      title={`Mirror ${extraTitle[type]}`}
      description={extraDescription[type]}
      status={status}
      statusTone={isSelected ? 'on' : 'off'}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      headerControl={
        <AppSwitch
          checked={isSelected}
          onChange={(_, checked) => setValue(checkboxValues[type], checked)}
          inputProps={{ 'aria-label': `Mirror ${extraTitle[type]}` }}
        />
      }
    >
      <Box display="flex" flexDirection="column" gridGap={8}>
        <Typography className={classes.hintText}>{extraText[type]}</Typography>

        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <FormControl>
              <AppSelect className={classes.selectRoot} {...field}>
                {options?.map(item => (
                  <MenuItem key={item.value} value={item.value}>
                    <span>{item.name}</span>
                  </MenuItem>
                ))}
              </AppSelect>
            </FormControl>
          )}
        />
      </Box>

      <ControlledAutocomplete<PaySchedule | Site>
        name={selectName}
        label={selectLabel[type]}
        items={items}
        multiple
      />
    </SettingsCard>
  );
};
